import { useCallback, useMemo } from "react";

import { useTheme } from "styled-components";
import { ITheme } from "@evergis/ui";

import { FEATURE_CARD_OTHER_COLOR, FILTERED_VALUE_OPACITY } from "../constants";
import { transparentizeColor } from "../../../utils/color";
import { getFilterValue } from "../utils/getFilterValue";
import { isEmptyValue } from "../utils/isEmptyValue";
import { isNumeric } from "../../../utils/string";
import { getConfigFilter } from "../utils/getConfigFilter";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { useWidgetConfiguration } from "../../Dashboard/hooks/useWidgetConfiguration";
import { FilterItem, SelectedFilter, WidgetType } from "../types";

const getMinMaxFromStringValue = (
  items: FilterItem[],
  value: { min: string; max: string },
  current: FilterItem,
  type: "min" | "max",
) => {
  const valueIndex = items.findIndex(item => item.value === (type === "min" ? value.min : value.max));
  const currentIndex = items.findIndex(item => item.value === (type === "min" ? value.min : value.max));

  return type === "min"
    ? currentIndex < valueIndex
      ? current.min
      : value.min
    : currentIndex >= valueIndex
    ? current.max
    : value.max;
};

export const useFeatureFilters = (type: WidgetType, filterName: string, items?: FilterItem[]) => {
  const theme = useTheme() as ITheme;
  const { filters, changeFilters } = useWidgetContext(type);
  const { currentConfig } = useWidgetConfiguration(type);

  const configFilter = useMemo(
    () => getConfigFilter(filterName, currentConfig.filters),
    [currentConfig.filters, filterName],
  );

  const hasAnyFilter = Array.isArray(filters[filterName]?.value)
    ? !!(filters[filterName].value as string[]).length
    : !!filters[filterName]?.value;

  const isFiltered = useCallback(
    (name: string) => {
      const filterValue = filters[filterName]?.value || configFilter?.defaultValue;

      if (!Array.isArray(filterValue)) {
        return filterValue === name;
      }

      return Boolean(
        (filterValue as string[]).length &&
          (configFilter?.valueType === "array"
            ? (filterValue as string[]).includes(name)
            : (filterValue as string[])?.[0] <= name && (filterValue as string[])?.[1] >= name),
      );
    },
    [configFilter?.defaultValue, configFilter?.valueType, filterName, filters],
  );

  const formatFilterColor = useCallback(
    (name: string, color: string, defaultColor = FEATURE_CARD_OTHER_COLOR) => {
      return isFiltered(name as string)
        ? color || theme.palette.primary
        : hasAnyFilter
        ? color
          ? transparentizeColor(color, FILTERED_VALUE_OPACITY)
          : defaultColor
        : color || theme.palette.primary;
    },
    [hasAnyFilter, isFiltered, theme.palette.primary],
  );

  const onFilter = useCallback(
    (newValue: string) => {
      const value = getFilterValue({
        selectedFilters: filters,
        configFilters: currentConfig.filters,
        filterName,
        newValue,
      });

      if (["single", "range"].includes(configFilter?.valueType)) {
        const filterProps: SelectedFilter = { value };

        if (Array.isArray(value)) {
          filterProps.min = value[0];
          filterProps.max = value[1] || value[0];
        }

        changeFilters({ [filterName]: filterProps }, configFilter?.resetFilters);

        return;
      }

      const data = items?.filter(item => value.includes(item.name));

      const { min, max } =
        data?.reduce(
          (result, current) => {
            const isNumericValue = isNumeric(current.min) && isNumeric(current.max);
            const currentMin = isNumericValue ? Number(current.min) : current.min;
            const currentMax = isNumericValue ? Number(current.max) : current.max;

            return {
              min:
                result.min === null
                  ? isEmptyValue(current.min)
                    ? null
                    : currentMin
                  : isNumericValue
                  ? Math.min(result.min, currentMin as number)
                  : getMinMaxFromStringValue(items, result, current, "min"),
              max:
                result.max === null
                  ? isEmptyValue(current.max)
                    ? null
                    : currentMax
                  : isNumericValue
                  ? Math.max(result.max, currentMax as number)
                  : getMinMaxFromStringValue(items, result, current, "max"),
            };
          },
          { min: null, max: null },
        ) || {};

      changeFilters({ [filterName]: { value, min, max } }, configFilter?.resetFilters);
    },
    [
      filters,
      currentConfig.filters,
      filterName,
      items,
      configFilter?.valueType,
      configFilter?.resetFilters,
      changeFilters,
    ],
  );

  return { filters, formatFilterColor, hasAnyFilter, isFiltered, onFilter };
};
